// EtaCard — Phase 4 Plan 04-07 (UI-SPEC §Screen 4 Overview tab).
//
// Headline ETA datetime + Caption confidence interval + optional border-buffer
// note + optional Override button (owner-only). Phase 4 MVP: override button
// is a placeholder (POST endpoint not yet implemented per UI-SPEC §EtaCard
// §Override action — deferred to Phase 5).

import { useTranslation } from "react-i18next";

import { TYPOGRAPHY } from "../lib/tokens";

export interface EtaCardProps {
  /** ISO 8601 timestamp of arrival ETA, or null when ETA not yet computed. */
  arrivalAt: string | null;
  /** "±2h" or similar narrative confidence label, optional. */
  confidence?: string | null;
  /** Border buffer narrative (e.g. {hours:3, border:"Kapıkule"}), optional. */
  borderBuffer?: { hours: number; border: string } | null;
  /** Owner-only flag — shows disabled "Override ETA" button when true. */
  ownerCanOverride?: boolean;
}

function formatEta(iso: string | null): string {
  if (iso === null) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function EtaCard({
  arrivalAt,
  confidence,
  borderBuffer,
  ownerCanOverride = false,
}: EtaCardProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      data-testid="eta-card"
      style={{
        padding: 12,
        border: "1px solid var(--tg-theme-hint-color)",
        borderRadius: 8,
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 12, color: "var(--tg-theme-hint-color)", marginBottom: 4 }}>
        {t("dispatcher:trip.eta.label", "Estimated arrival") as string}
      </div>
      <div
        data-testid="eta-card-time"
        style={{
          fontSize: TYPOGRAPHY.display.size,
          fontWeight: TYPOGRAPHY.display.weight,
          lineHeight: TYPOGRAPHY.display.lineHeight,
          color: "var(--tg-theme-text-color)",
        }}
      >
        {formatEta(arrivalAt)}
      </div>
      {confidence !== null && confidence !== undefined && (
        <div
          data-testid="eta-card-confidence"
          style={{ fontSize: 12, color: "var(--tg-theme-hint-color)", marginTop: 4 }}
        >
          {confidence}
        </div>
      )}
      {borderBuffer && (
        <div
          data-testid="eta-card-border-buffer"
          style={{ fontSize: 12, color: "var(--tg-theme-hint-color)", marginTop: 4 }}
        >
          {
            t("dispatcher:trip.eta.border_buffer", {
              hours: borderBuffer.hours,
              border: borderBuffer.border,
            }) as string
          }
        </div>
      )}
      {ownerCanOverride && (
        <button
          type="button"
          data-testid="eta-card-override-btn"
          disabled
          title={t("dispatcher:trip.eta.override_unavailable", "Coming in Phase 5") as string}
          style={{
            marginTop: 8,
            padding: "6px 10px",
            border: "1px solid var(--tg-theme-hint-color)",
            borderRadius: 6,
            background: "transparent",
            color: "var(--tg-theme-hint-color)",
            cursor: "not-allowed",
          }}
        >
          {t("dispatcher:trip.eta.override", "Override ETA") as string}
        </button>
      )}
    </div>
  );
}
